import { Component } from "react";
import { RefreshCw, Home, AlertTriangle } from "lucide-react";

const LOGO     = "/assets/images/kiddo-logo.png";
const WORDMARK = "/assets/images/kiddo-wordmark.png";

/**
 * OopsPage — branded crash fallback rendered by ErrorBoundary.
 * Deliberately uses no React Router (the error may have killed the router tree).
 */
function OopsPage({ onRetry }) {
  return (
    <div
      style={{ minHeight: "100dvh", background: "#FDF8F2" }}
      className="flex flex-col items-center justify-center px-5 py-16 text-center"
      data-testid="error-boundary-page"
    >
      {/* Brand strip at top */}
      <div className="flex items-center gap-2 mb-10">
        <img src={LOGO} alt="Kiddo+" className="w-10 h-10 rounded-[22%]" />
        <img src={WORDMARK} alt="Kiddo+" className="h-8 w-auto" />
      </div>

      {/* Illustration */}
      <div
        className="w-20 h-20 rounded-full flex items-center justify-center mb-6 shadow-lg"
        style={{ background: "linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)" }}
      >
        <AlertTriangle className="w-9 h-9" style={{ color: "#F4845F" }} />
      </div>

      {/* Heading */}
      <h1
        className="text-3xl sm:text-4xl font-black mb-3 leading-tight"
        style={{ color: "#2A1F3D", letterSpacing: "-0.03em" }}
      >
        Oops! Something went wrong
      </h1>

      {/* Warm message */}
      <p
        className="text-base leading-relaxed max-w-sm mb-8"
        style={{ color: "#5C4F6E" }}
      >
        We hit an unexpected bump. Don't worry — your child's data is safe.
        Let's get you back on track.
      </p>

      {/* CTAs */}
      <div className="flex flex-col sm:flex-row gap-3 w-full max-w-xs">
        <a
          href="/activities"
          data-testid="error-boundary-dashboard-btn"
          className="flex items-center justify-center gap-2 rounded-full font-bold text-sm px-6 py-3 transition-all active:scale-95 shadow-sm text-white"
          style={{ background: "#2A7B6F" }}
        >
          <Home className="w-4 h-4" />
          Go to Dashboard
        </a>
        <button
          onClick={onRetry}
          data-testid="error-boundary-retry-btn"
          className="flex items-center justify-center gap-2 rounded-full font-bold text-sm px-6 py-3 border-2 transition-all active:scale-95"
          style={{ borderColor: "#2A7B6F", color: "#2A7B6F", background: "white" }}
        >
          <RefreshCw className="w-4 h-4" />
          Try again
        </button>
      </div>

      {/* Support link */}
      <p className="mt-8 text-xs" style={{ color: "#9E8FAF" }}>
        Still seeing this?{" "}
        <a
          href="mailto:support@kiddoplus.com"
          className="underline underline-offset-2 hover:opacity-75 transition-opacity"
          style={{ color: "#2A7B6F" }}
        >
          Contact our support team
        </a>
      </p>

      {/* Subtle footer */}
      <p className="mt-10 text-[10px]" style={{ color: "#C4B8D4" }}>
        Kiddo+ · Understand · Support · Celebrate
      </p>
    </div>
  );
}

/**
 * ErrorBoundary — catches uncaught render-phase errors anywhere in the tree.
 * Must be a class component (React limitation for error boundaries).
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
    this.handleRetry = this.handleRetry.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log for debugging without exposing to users
    if (typeof console !== "undefined") {
      console.error("[Kiddo+ ErrorBoundary] Uncaught render error:", error, info?.componentStack);
    }
  }

  handleRetry() {
    this.setState({ hasError: false, error: null });
    // Hard-reload as a last resort to clear broken state
    try {
      window.location.reload();
    } catch { /* ignore */ }
  }

  render() {
    if (this.state.hasError) {
      return <OopsPage onRetry={this.handleRetry} />;
    }
    return this.props.children;
  }
}
